/**
 * Pure helpers for the per-town "sell without an estate agent" pages.
 *
 * Kept dependency-free so they can be imported by BOTH the React page
 * (src/pages/SellInTownPage.jsx) and the static build renderer
 * (scripts/render-sell-pages.mjs) — one source of truth for the savings maths
 * and the sibling-town interlinking, so the crawler HTML and the SPA agree.
 */

/**
 * Representative UK estate-agent commission. Industry trackers (GetAgent /
 * TheAdvisory) put the national average sole-agency fee around 1.42% including
 * VAT, so we use that as a defensible "typical" figure and round the resulting
 * fee to the nearest £50 (it is an estimate, not a quote).
 */
export const TYPICAL_AGENT_FEE_PCT = 1.42

/**
 * Estimate the estate-agent commission on a sale at `price`, rounded to the
 * nearest £50. Returns 0 for non-positive / non-finite input so callers can
 * safely hide the savings hook when data is missing.
 *
 * @param {number} price - sale price (e.g. a town's median sold price)
 * @param {number} [pct] - commission percentage (default TYPICAL_AGENT_FEE_PCT)
 * @returns {number} estimated fee in whole pounds, rounded to nearest £50
 */
export const estimateAgentFee = (price, pct = TYPICAL_AGENT_FEE_PCT) => {
  if (!Number.isFinite(price) || price <= 0) return 0
  return Math.round((price * (pct / 100)) / 50) * 50
}

/**
 * The spread of sole-agency fees UK sellers actually get quoted, used by the
 * savings calculator so the reader sees a range rather than a single number
 * they can dismiss. `typical` intentionally matches TYPICAL_AGENT_FEE_PCT so
 * the calculator's default agrees with the rest of the page.
 */
export const AGENT_FEE_RANGE = [
  { pct: 1.0, label: 'Low (1.0%)' },
  { pct: TYPICAL_AGENT_FEE_PCT, label: `Typical (${TYPICAL_AGENT_FEE_PCT}%)` },
  { pct: 1.8, label: 'Above average (1.8%)' },
  { pct: 3.0, label: 'High (3.0%)' },
]

/** Bounds for the calculator's price input, in whole pounds. */
export const CALC_PRICE_MIN = 50000
export const CALC_PRICE_MAX = 2000000
export const CALC_PRICE_STEP = 5000

/**
 * Clamp a user-entered price into the calculator's supported range. Non-finite
 * or non-positive input falls back to `fallback` so the UI never renders NaN.
 *
 * @param {number} price
 * @param {number} [fallback] - value to use when `price` is unusable
 * @returns {number}
 */
export const clampCalcPrice = (price, fallback = 250000) => {
  const n = Number(price)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return Math.min(CALC_PRICE_MAX, Math.max(CALC_PRICE_MIN, Math.round(n)))
}

/**
 * The full savings picture for one asking price: what each fee band would cost
 * with an agent, and what the same sale costs on Keyzee (nothing).
 *
 * @param {number} price
 * @returns {{price:number, keyzeeFee:number, rows:Array<{pct:number,label:string,fee:number}>}}
 */
export const buildSavingsBreakdown = (price) => {
  const safePrice = clampCalcPrice(price)
  return {
    price: safePrice,
    keyzeeFee: 0,
    rows: AGENT_FEE_RANGE.map(({ pct, label }) => ({
      pct,
      label,
      fee: estimateAgentFee(safePrice, pct),
    })),
  }
}

/**
 * Pick a deterministic set of OTHER towns to interlink from a town page,
 * for crawl-depth and internal PageRank. Towns are ordered alphabetically and
 * we take a window starting just after the current town (wrapping around) so
 * each page links to a different, stable slice of neighbours.
 *
 * @param {Array<{slug:string,name:string,kind?:string}>} towns - region index entries
 * @param {string} currentSlug - slug of the town being rendered (excluded)
 * @param {number} [count] - how many links to return
 * @returns {Array<{slug:string,name:string}>}
 */
export const pickNearbyTowns = (towns, currentSlug, count = 6) => {
  const all = (Array.isArray(towns) ? towns : [])
    .filter((t) => t && t.slug && (t.kind === undefined || t.kind === 'town'))
    .sort((a, b) => a.name.localeCompare(b.name))

  const others = all.filter((t) => t.slug !== currentSlug)
  if (others.length <= count) return others

  const pos = all.findIndex((t) => t.slug === currentSlug)
  const start = pos === -1 ? 0 : pos
  const out = []
  for (let i = 0; i < count; i += 1) {
    out.push(others[(start + i) % others.length])
  }
  return out
}
